import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { api } from '../services/api.js';
import { getError } from '../utils/errorMessages.js';

const Contact = () => {
  const { subscription } = useSubscription();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.post('/contact', formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      // Get user-friendly error message
      const errorMessage = getError(error, 'Unable to send your message. Please try again or email us directly at support@elderfitsecrets.com');
      setSubmitStatus(`error:${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-lg mb-4">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Contact Support</h1>
            <p className="text-xl text-gray-600">We're here to help! Send us a message and we'll get back to you.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Send us a Message</h2>
              
              {submitStatus === 'success' ? (
                <div className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-4 rounded-lg">
                  <p className="font-bold text-lg mb-2">Thank you for your message!</p>
                  <p>We'll get back to you as soon as possible. You'll receive a confirmation email shortly.</p>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <span>📧</span>
                    <span>Keep our emails out of your spam folder</span>
                  </p>
                  <p className="text-sm text-blue-800">
                    Please check your spam folder for our confirmation email. If you find it there, mark it as "Not Spam" and add <strong>noreply@elderfitsecrets.com</strong> and <strong>support@elderfitsecrets.com</strong> to your contacts. This helps ensure you receive all important updates from ElderFit Secrets.
                  </p>
                </div>
                </div>
              ) : submitStatus?.startsWith('error:') ? (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-4 rounded-lg">
                  {submitStatus.replace('error:', '') || 'Failed to send message. Please try again or email us directly at support@elderfitsecrets.com'}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                </form>
              )}
            </div>
            
            {/* Support Information */}
            <div className="space-y-6">
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Support Information</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Support
                  </h3>
                  <p className="text-gray-700 font-medium">support@elderfitsecrets.com</p>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Response Time
                  </h3>
                  <p className="text-gray-700 font-medium">
                    {subscription?.tier === 'elite' ? '12 hours' :
                     subscription?.tier === 'premium' ? '24 hours' :
                     '48 hours'}
                  </p>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Support Hours
                  </h3>
                  <p className="text-gray-700 font-medium">Monday - Friday: 9:00 AM - 5:00 PM EST</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-indigo-200 shadow-lg">
              <h3 className="font-bold text-indigo-900 mb-3 text-lg">Need Immediate Help?</h3>
              <p className="text-indigo-800 mb-4">
                Check our <Link to="/help" className="underline font-semibold hover:text-indigo-900">Help Center</Link> for quick answers to common questions.
              </p>
              <Link
                to="/faq"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-bold transition-colors group"
              >
                View FAQs
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 